import axios from 'axios';

import type { CustomField } from 'bundles/apex/custom_fields/types';
import type { IBulkRowHook, IDeveloperField, IReviewStepData, IRowHookInput, IStepHook } from 'dromo-uploader-js';

type StepHookParams = Parameters<IStepHook['callback']>;
type Dromo = StepHookParams[0];
type StepHookData = StepHookParams[1];

type Query = string | number;
type IdsByQuery = Record<Query, number>;

interface Keys {
  idKey: string;
  queryKey: string;
}

interface HookOptions {
  force?: boolean;
}

export function buildContactIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_contact_ids`, options);
}

export function buildPropertyIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_property_ids`, options);
}

export function buildUserIdLookupHooks(label: string, keys: Keys, options?: HookOptions) {
  return buildIdLookupHooks(label, keys, `/crm/imports/find_user_ids`, options);
}

function buildIdLookupHooks(label: string, keys: Keys, path: string, options: HookOptions = {}): {
  stepHook: IStepHook;
  bulkRowHook: IBulkRowHook;
} {
  return {
    stepHook: {
      type: 'REVIEW_STEP',
      callback: (instance, data) => {
        addIdField(instance, data, label, keys, options);
      },
    },
    bulkRowHook: (records: IRowHookInput[]) => {
      return loadIds(records, keys, path, options);
    },
  };
}

function addIdField(instance: Dromo, looselyTypedData: StepHookData, label: string, keys: Keys, options: HookOptions) {
  const data = looselyTypedData as IReviewStepData;
  if (!data.fields[keys.idKey] && (data.fields[keys.queryKey] || options.force)) {
    const field: IDeveloperField = {
      key: keys.idKey,
      label,
      type: ['number', 'integer'],
    };

    if (options.force) {
      field.validators = [{ validate: 'required' }];
    }

    // TODO: Could make this a dropdown, so the user can:
    //       * Choose an ID (might be multiple matches)
    //       * Choose none to create a new record
    instance.addField(field, { before: keys.queryKey });
  }
}

async function loadIds(records: IRowHookInput[], keys: Keys, path: string, options: HookOptions) {
  if (!records.length || !records[0].row.hasOwnProperty(keys.idKey)) {
    return records;
  }

  for (const record of records) {
    record.row[keys.idKey].info = [];
  }

  const queries = getQueries(records, keys);

  if (queries.length) {
    const response = await axios.post(path, { queries });
    const idsByQuery = response.data as IdsByQuery;
    for (const record of records) {
      setId(record, idsByQuery, keys, options);
    }
  }

  return records;
}

function getQueries(records: IRowHookInput[], keys: Keys): Query[] {
  const queries = new Set<string | number>();
  for (const record of records) {
    const query = record.row[keys.queryKey]?.value;
    const existingId = record.row[keys.idKey]?.resultValue;
    if (existingId) {
      queries.add(existingId);
    } else if (query) {
      queries.add(query);
    }
  }

  return [...queries];
}

function setId(record: IRowHookInput, idsByQuery: IdsByQuery, keys: Keys, options: HookOptions) {
  const idCell = record.row[keys.idKey];
  const existingId = idCell?.resultValue || null;

  let foundId: number;
  if (existingId) {
    foundId = idsByQuery[existingId];
  } else {
    const queryCell = record.row[keys.queryKey];
    if (!queryCell) {
      return;
    }

    foundId = idsByQuery[queryCell?.value];
  }

  idCell.value = idCell.resultValue = foundId || existingId;

  if (!foundId) {
    idCell.info = [{
      level: options.force ? 'error' : 'warning',
      message: 'Not found',
    }];
  }
}

export function mapCustomFieldsToDromoFields(customFields: CustomField[]): IDeveloperField[] {
  return customFields.map(customField => {
    let options, type;
    if (customField.type === 'currency') {
      type = ['number', 'usd'];
    } else if (customField.type === 'date') {
      type = ['date', { displayFormat: 'MM/dd/yyyy' }];
    } else if (customField.type === 'float' || customField.type === 'number') {
      type = 'number';
    } else if (customField.type === 'percentage') {
      type = ['number', 'percent'];
    } else if (customField.options?.length) {
      type = 'select';
      options = customField.options.map(option => ({ label: option, value: option }));
    } else {
      type = 'string';
    }

    return {
      key: customField.name,
      label: customField.label,
      selectOptions: options,
      type,
    };
  });
}

export function addDefaultValidators(fields: IDeveloperField[]) {
  for (const field of fields) {
    field.validators = field.validators || [];

    if (field.type === 'string' && !field.validators.find(validator => validator.validate === 'length')) {
      field.validators.push({ validate: 'length', max: 255 });
    }
  }

  return fields;
}
